import { GanttData, Loom, WarpBeam, ClothPiece, CalendarInfo } from '../types';
import { add, addHours, addDays, addMinutes } from 'date-fns';

// Generatore pseudo-casuale con seed (mulberry32): a parità di seed i dati sono sempre gli stessi,
// così la grafica è riproducibile (bug, screenshot). Cambia il seed con ?seed=N nell'URL.
const DEFAULT_SEED = 20260101;
let rng: () => number = Math.random;

const createRng = (seed: number) => {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

const getSeed = (): number => {
    const param = new URLSearchParams(window.location.search).get('seed');
    const parsed = param === null ? NaN : Number(param);
    return Number.isFinite(parsed) ? parsed : DEFAULT_SEED;
};

const random = (min: number, max: number) => Math.floor(rng() * (max - min + 1)) + min;
const randomElement = <T,>(arr: T[]): T => arr[Math.floor(rng() * arr.length)];
const withRandomMinutes = (date: Date, min: number, max: number) => addMinutes(addHours(date, random(min, max)), random(0, 59));

const loomIds = ['A01', 'A02', 'B01', 'B02', 'B03', 'C04', 'C05', 'D06', 'D07', 'M08'];

const LOOM_STOP_STATES_WITH_ACTIVE_BEAM: Array<{ color: string; textColor: string; description: string }> = [
    { color: '#FF0000', textColor: '#ffffff', description: 'Rottura Ordio' },
    { color: '#FF6600', textColor: '#ffffff', description: 'Rottura Trama' },
    { color: '#9933CC', textColor: '#ffffff', description: 'Manutenzione' },
    { color: '#CC8800', textColor: '#ffffff', description: 'Fermo Manuale' },
];
const LOOM_NO_QUEUE_STATE = { color: '#888888', textColor: '#ffffff', description: 'No Coda' };
const LOOM_OK_STATE = { color: '#00AA44', textColor: '#ffffff', description: '' };
const articles = ['ART7070', 'ART8020', 'ART9515', 'ARTZ100', 'ARTZ200', 'ART1234'];
const orditos = ['ORD111', 'ORD222', 'ORD333', 'ORD444'];
const flanges = ['F80', 'F90', 'F100', 'F110'];
const operators = ['OP1', 'OP2', 'OP3'];

const WPB_STATUS_MAP: Record<WarpBeam['status'], { bgColor: string, textColor: string, statusClass: string }> = {
    D: { bgColor: 'bg-[#FF3300]', textColor: 'text-white', statusClass: '.StD' },
    L: { bgColor: 'bg-[#3366FF]', textColor: 'text-white', statusClass: '.StL' },
    V: { bgColor: 'bg-gray-700', textColor: 'text-white', statusClass: '.StV' },
    T: { bgColor: 'bg-[#A0A0A0]', textColor: 'text-white', statusClass: '.StT' },
};

const CP_STATUS_MAP: Record<ClothPiece['status'], { bgColor: string, textColor: string, statusClass: string }> = {
    D: { bgColor: 'bg-[#FF00CC]', textColor: 'text-white', statusClass: '.StD' },
    L: { bgColor: 'bg-[#33FFCC]', textColor: 'text-black', statusClass: '.StL' },
    P: { bgColor: 'bg-[#C0C0C0]', textColor: 'text-black', statusClass: '.StP' },
    Fin: { bgColor: 'bg-[#C09030]', textColor: 'text-white', statusClass: '.StFin' },
};


// Probabilità che un telaio con subbio attivo sia in stato di fermo (vedi CLAUDE.md: circa 25%)
const LOOM_STOP_PROBABILITY = 0.25;
// Fuori servizio (manutenzione lunga) tra due subbi: raro, i telai lavorano quasi sempre
const OUT_OF_SERVICE_PROBABILITY = 0.06;
// Telai senza articoli accodati dopo quello in lavorazione
const SHORT_QUEUE_PROBABILITY = 0.15;
const MAX_BEAM_SETS = 24;

const generateMockData = (): GanttData => {
    rng = createRng(getSeed());
    const looms: Record<string, Loom> = {};
    const warpBeamsByLoom: Record<string, WarpBeam[]> = {};
    const clothPiecesByWarpBeam: Record<string, ClothPiece[]> = {};
    const calendar: CalendarInfo = {};
    
    // Populate some non-working days
    const day = add(new Date(), { days: -90 });
    for(let i=0; i<180; i++) {
        if(rng() < 0.05) { // 5% chance of being a non-working day
             calendar[addDays(day, i).toISOString().split('T')[0]] = { isNonWorking: true };
        }
    }


    loomIds.forEach((id, index) => {
        const totalSubbi = randomElement([1, 2]) as (1 | 2);
        const loomStart = add(new Date(), { days: -random(10, 40), hours: -random(0, 23), minutes: -random(0, 59) });
        const loomEnd = add(loomStart, { days: random(30, 90), hours: random(0, 23), minutes: random(0, 59) });

        looms[id] = {
            id,
            name: id,
            type: `TYPE-${id.charAt(0)}`,
            status: LOOM_OK_STATE,
            calendarId: `CAL${index % 3 + 1}`,
            totalSubbi,
            start: loomStart,
            end: loomEnd,
            metersNow: random(1000, 5000),
        };
        warpBeamsByLoom[id] = [];

        // Storico continuo: si parte prima dell'inizio dell'intervallo visibile (-60 giorni) e si
        // accodano subbi uno dopo l'altro. I telai lavorano quasi sempre: tra un subbio e il
        // successivo passano solo poche ore (cambio subbio), salvo rari fuori servizio.
        const now0 = new Date();
        let lastWarpBeamEnd = add(now0, { days: -random(66, 85), hours: -random(0, 23), minutes: -random(0, 59) });
        // Coda futura: di solito lunga, ogni tanto vuota ("No Coda": nessun articolo accodato)
        const hasShortQueue = rng() < SHORT_QUEUE_PROBABILITY;
        const queueHorizon = addDays(now0, hasShortQueue ? 0 : random(30, 85));

        for (let i = 0; i < MAX_BEAM_SETS; i++) { // For each set of (potentially parallel) warp beams
            if (lastWarpBeamEnd > now0 && lastWarpBeamEnd > queueHorizon) break;

            const outOfService = rng() < OUT_OF_SERVICE_PROBABILITY;
            const beamStart = outOfService
                ? withRandomMinutes(addDays(lastWarpBeamEnd, random(2, 6)), 0, 12) // fuori servizio: fermo qualche giorno
                : withRandomMinutes(lastWarpBeamEnd, 1, 6); // cambio subbio: poche ore
            let maxBeamEnd = beamStart;
            
            for (let j = 1; j <= totalSubbi; j++) {
                const beamMetersTotal = random(2000, 5000);
                const warpBeamId = `OT${id}-${i}-${j}`;
                clothPiecesByWarpBeam[warpBeamId] = [];
                
                const numPieces = random(1, 3);
                let remainingMeters = beamMetersTotal;
                let pieceStart = beamStart;
                let currentBeamEnd = beamStart;

                for (let k = 0; k < numPieces; k++) {
                    let pieceMeters: number;
                    if (k === numPieces - 1) {
                        pieceMeters = remainingMeters;
                    } else {
                        // The main piece will be the last one or we just give small portions to the others
                        // Give 10% to 20% to the minor pieces, leaving the bulk for the main one
                        const minorShare = Math.floor(beamMetersTotal * (random(10, 20) / 100));
                        pieceMeters = Math.min(remainingMeters, minorShare);
                    }
                    if (pieceMeters <= 0) break;
                    
                    remainingMeters -= pieceMeters;
                    
                    const density = random(18, 30); // 18-30 colpi/cm
                    const effectiveSpeed = 360 * 0.90; // 360 colpi/min at 90% efficiency
                    const pieceDurationMinutes = Math.round((pieceMeters * 100 * density) / effectiveSpeed);
                    
                    const pieceEnd = addMinutes(pieceStart, pieceDurationMinutes);
                    currentBeamEnd = pieceEnd;
                    
                    const now = new Date();
                    let cpStatus: ClothPiece['status'];
                    if (pieceEnd < now) cpStatus = 'P';
                    else if (pieceStart > now) cpStatus = 'D';
                    else cpStatus = 'L';
                    
                    let pieceProduced = pieceMeters;
                    if (cpStatus === 'D') pieceProduced = 0;
                    else if (cpStatus === 'L') {
                        const totalDur = pieceEnd.getTime() - pieceStart.getTime();
                        const elaps = now.getTime() - pieceStart.getTime();
                        pieceProduced = Math.round(pieceMeters * (elaps / totalDur));
                    }
                    
                    const article = randomElement(articles);

                    const piece: ClothPiece = {
                        id: `LS${warpBeamId}-${k}`,
                        warpBeamId,
                        parallelPosition: j as (1 | 2),
                        article,
                        articleBase: article.split('Z')[0],
                        group: `GR${random(100, 999)}`,
                        start: pieceStart,
                        end: pieceEnd,
                        status: cpStatus,
                        displayStatus: CP_STATUS_MAP[cpStatus],
                        metersStart: pieceProduced,
                        metersTotal: pieceMeters,
                        annodatura: `AN${random(10, 50)}`,
                        flange: '', // will set after beam creation
                        notes: {
                            caricaSubbi: rng() > 0.8 ? 'Nota carica subbi...' : undefined,
                            speciale: rng() > 0.9 ? 'Nota speciale...' : undefined,
                        },
                        dischargeDate: rng() > 0.5 ? addMinutes(addHours(pieceEnd, random(1, 3)), random(0, 59)) : undefined,
                        dischargeOperator: rng() > 0.5 ? randomElement(operators) : undefined,
                    };
                    clothPiecesByWarpBeam[warpBeamId].push(piece);
                    
                    if (remainingMeters > 0) {
                        pieceStart = addMinutes(pieceEnd, random(15, 30)); // 15-30 min gap for piece change
                    }
                }
                
                if (currentBeamEnd > maxBeamEnd) maxBeamEnd = currentBeamEnd;
                
                const now = new Date();
                const hasRunningPiece = clothPiecesByWarpBeam[warpBeamId].some(p => p.status === 'L');
                let wpbStatus: WarpBeam['status'];
                if (currentBeamEnd < now) wpbStatus = 'T';
                else if (beamStart > now) wpbStatus = 'D';
                else wpbStatus = hasRunningPiece ? 'L' : 'V';

                const warpBeam: WarpBeam = {
                    id: warpBeamId,
                    loomId: id,
                    pairId: `C-${id}-${i}`,
                    parallelPosition: j as (1 | 2),
                    ordito: randomElement(orditos),
                    sb: `SB${random(1000,9999)}`,
                    start: beamStart,
                    end: currentBeamEnd,
                    status: wpbStatus,
                    displayStatus: WPB_STATUS_MAP[wpbStatus],
                    metersStart: clothPiecesByWarpBeam[warpBeamId].reduce((sum, p) => sum + p.metersStart, 0),
                    metersTotal: beamMetersTotal,
                    flange: randomElement(flanges)
                };
                warpBeamsByLoom[id].push(warpBeam);
                
                // Set the correct flange for pieces
                clothPiecesByWarpBeam[warpBeamId].forEach(p => p.flange = warpBeam.flange);
            }
            lastWarpBeamEnd = maxBeamEnd;
        }

        const loomBeams = warpBeamsByLoom[id];
        const nowRef = new Date();
        const hasActiveBeam = loomBeams.some(wb => wb.status === 'L' || wb.status === 'V');
        const hasQueue = loomBeams.some(wb => wb.start > nowRef);

        // Inizio/fine del telaio coerenti con la programmazione generata
        looms[id].start = new Date(Math.min(...loomBeams.map(wb => wb.start.getTime())));
        looms[id].end = new Date(Math.max(...loomBeams.map(wb => wb.end.getTime())));

        looms[id].status = !hasActiveBeam || !hasQueue
            ? LOOM_NO_QUEUE_STATE
            : (rng() < LOOM_STOP_PROBABILITY ? randomElement(LOOM_STOP_STATES_WITH_ACTIVE_BEAM) : LOOM_OK_STATE);
    });

    return { looms, warpBeamsByLoom, clothPiecesByWarpBeam, calendar };
};


export const mockGanttService = {
    fetchAllData: (): Promise<GanttData> => {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve(generateMockData());
            }, 500); // Simulate network delay
        });
    }
};
